import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, ShieldAlert, Check, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SectionTitle } from "@/components/Bits";

// Modes that ROUTE REAL ORDERS — every gate must pass before save.
const LIVE_MODES = ["live_enabled", "lbank_live_enabled"];
// Maps a LIVE bot_mode to the exchange the preflight test must target.
const MODE_EXCHANGE = {
  live_enabled: "binance_live",
  lbank_live_enabled: "lbank",
};

// Result shape for each preflight gate so the UI can render them
// uniformly (✓ green, ✗ red) and so the save button can hard-block when
// any gate is failing. `error` is operator-facing copy.
const gateInit = () => ({
  state: "idle",  // 'idle' | 'running' | 'pass' | 'fail'
  message: "",
});

export default function BotSettings() {
  const [s, setS] = useState(null);            // current bot_settings (form-bound)
  const [originalMode, setOriginalMode] = useState(null);
  const [activeStrategy, setActiveStrategy] = useState(null); // {active_strategy_id, profile}
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState(null); // the live mode the user is switching to
  const [confirmText, setConfirmText] = useState("");
  // 5 preflight gates required to switch to a live mode. Each is run on
  // demand when the user opens the confirm dialog so we never make
  // network calls on form-edit. The 5th gate (`profile_engine`) was
  // added after the production incident where an activated CVD GO
  // strategy profile sat dormant because the live engine was still
  // evaluating the legacy `entry_rules` dict.
  const [gates, setGates] = useState({
    server_flag: gateInit(),       // LIVE_TRADING_ENABLED=true on the server
    connection: gateInit(),        // /api/exchange/test?exchange=<target> succeeds
    active_strategy: gateInit(),   // active_strategy_id is set
    profile_engine: gateInit(),    // USE_STRATEGY_PROFILE_ENGINE=true on the server
    balance: gateInit(),           // last_total_wallet_balance > 0 for the target
  });
  const [running, setRunning] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const cur = await api.getSettings();
      setS(cur);
      setOriginalMode(cur.bot_mode);
      try {
        const a = await api.getActiveStrategy();
        setActiveStrategy(a);
      } catch { /* non-fatal — banner shows generic warning */ }
    })();
  }, []);

  const isLiveTarget = useMemo(() => LIVE_MODES.includes(s?.bot_mode), [s?.bot_mode]);
  const switchingToLive = isLiveTarget && originalMode !== s?.bot_mode;
  const allGatesPass = Object.values(gates).every((g) => g.state === "pass");

  // Run all 4 preflight gates in parallel against the chosen live target.
  // Read-only — no orders, no engine state mutation. Each gate sets its
  // own pass/fail/message; the operator sees a green/red checklist.
  const runPreflight = async (targetMode) => {
    setRunning(true);
    const target = MODE_EXCHANGE[targetMode];
    setGates({
      server_flag: { state: "running", message: "" },
      connection: { state: "running", message: "" },
      active_strategy: { state: "running", message: "" },
      profile_engine: { state: "running", message: "" },
      balance: { state: "running", message: "" },
    });
    const next = {};
    // Gate 1 + Gate 5: LIVE_TRADING_ENABLED env on the server, plus
    // USE_STRATEGY_PROFILE_ENGINE — both are read off the same
    // `/api/debug/live-status` snapshot so we don't double-request.
    try {
      const ds = await api.debugLiveStatus();
      next.server_flag = ds?.LIVE_TRADING_ENABLED_parsed
        ? { state: "pass", message: "LIVE_TRADING_ENABLED=true on the server" }
        : { state: "fail", message: "LIVE_TRADING_ENABLED is not 'true' on the server. Set it in the deploy panel and redeploy." };
      next.profile_engine = ds?.USE_STRATEGY_PROFILE_ENGINE_parsed
        ? { state: "pass", message: "Profile engine enabled — live evaluation will use the active strategy profile." }
        : {
            state: "fail",
            message:
              "Active strategy profile is selected, but the live engine is still using legacy entry_rules. " +
              "Set USE_STRATEGY_PROFILE_ENGINE=true and redeploy.",
          };
    } catch (e) {
      next.server_flag = { state: "fail", message: e?.response?.data?.detail || e.message || "live-status check failed" };
      next.profile_engine = { state: "fail", message: "live-status check failed — cannot read USE_STRATEGY_PROFILE_ENGINE" };
    }
    // Gate 2: per-exchange connection test (read-only)
    try {
      const t = await api.testConnection(target);
      next.connection = t.success
        ? { state: "pass", message: t.message?.slice(0, 140) || `Connected to ${target}` }
        : { state: "fail", message: t.message?.slice(0, 200) || `Connection test failed for ${target}` };
      // Gate 4: balance — ONLY trustworthy if the test we just ran is
      // the right exchange. Read out of the SAME response, not status,
      // so we can't accidentally use a stale snapshot from a different
      // exchange the user tested earlier.
      const wallet = t?.total_wallet_balance ?? null;  // backend may not include it on every shape; fall back below
      // Many adapters return it under different keys in the message
      // path. Re-fetch /bot/status as the canonical store, but only
      // accept it when last_tested_exchange matches.
      const bs = await api.status();
      const conn = bs?.exchange_connection || {};
      const tested = conn.last_tested_exchange;
      const w = (wallet !== null && wallet !== undefined)
        ? Number(wallet)
        : (tested === target ? Number(conn.last_total_wallet_balance ?? NaN) : NaN);
      if (Number.isFinite(w) && w > 0) {
        next.balance = { state: "pass", message: `Live balance: ${w.toFixed(2)} USDT on ${target}` };
      } else if (Number.isFinite(w)) {
        next.balance = { state: "fail", message: `Live balance is ${w.toFixed(2)} USDT — deposit funds before switching to live execution.` };
      } else {
        next.balance = { state: "fail", message: `Could not read live balance for ${target}. Click "Test connection" on the Exchange page first.` };
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "connection test failed";
      next.connection = { state: "fail", message: msg };
      next.balance = { state: "fail", message: "balance unavailable — connection test failed" };
    }
    // Gate 3: active strategy
    try {
      const a = await api.getActiveStrategy();
      setActiveStrategy(a);
      next.active_strategy = a?.active_strategy_id
        ? { state: "pass", message: `Active profile: ${a.profile?.name || a.active_strategy_id.slice(0, 8)}` }
        : { state: "fail", message: "No active strategy profile. Activate one on /strategies before switching to live." };
    } catch (e) {
      next.active_strategy = { state: "fail", message: e?.response?.data?.detail || e.message || "active strategy lookup failed" };
    }
    setGates(next);
    setRunning(false);
  };

  // Save handler — branches on whether a typed-confirmation is required.
  // For non-live transitions (paper/testnet/live_disabled) the dialog is
  // not shown. For live_enabled / lbank_live_enabled, the dialog opens
  // BEFORE the PUT — we never persist a live mode without the operator
  // typing "CONFIRM LIVE" and all 4 gates passing.
  const onSaveClick = async () => {
    if (switchingToLive) {
      setConfirmTarget(s.bot_mode);
      setConfirmOpen(true);
      setConfirmText("");
      await runPreflight(s.bot_mode);
      return;
    }
    await persistSave();
  };

  const persistSave = async () => {
    setSaving(true);
    try {
      const out = await api.putSettings(s);
      setS(out);
      setOriginalMode(out.bot_mode);
      toast.success("Bot settings saved");
      setConfirmOpen(false);
      // Broadcast a hint to Layout so it can refresh the top banner +
      // sidebar balance immediately instead of waiting up to 4s for the
      // next status poll. Receiver: `Layout.jsx` window event listener.
      try { window.dispatchEvent(new CustomEvent("bot-settings:saved")); } catch { /* SSR safety */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!s) return <div className="p-6 text-sm text-[#A0A0AB]">Loading…</div>;

  const noActiveStrategy = !activeStrategy?.active_strategy_id;

  return (
    <div className="p-6 space-y-6 max-w-3xl">
      <SectionTitle sub="MVP scope: BTC/USDT only. Hedge mode on live/testnet exchanges is controlled by the exchange itself — set it on the exchange's Preferences page. The toggle below only affects Paper Trading simulation.">
        Bot Settings
      </SectionTitle>

      {/* Active-strategy warning. Highly visible because the engine
          falls back to a hardcoded RSI rule when no profile is active —
          easy way for an operator to enable live trading and have the
          bot fire signals from a strategy they never reviewed. */}
      {noActiveStrategy ? (
        <div
          data-testid="bot-settings-no-active-strategy-warning"
          className="flex items-start gap-3 px-4 py-3 rounded-sm bg-[#F59E0B]/10 border border-[#F59E0B]/40 text-[#F59E0B] text-sm"
        >
          <AlertTriangle size={16} strokeWidth={2} className="mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <div className="font-semibold uppercase tracking-wider text-xs">No active strategy profile</div>
            <div className="text-[#F59E0B]/90">
              Engine will use the fallback hardcoded RSI rule. Activate a profile on{" "}
              <a href="/strategies" className="underline decoration-dotted hover:decoration-solid">Strategy Profiles →</a> before enabling live mode.
            </div>
          </div>
        </div>
      ) : activeStrategy?.profile_engine_enabled === false ? (
        // Profile selected but the live engine is still on the legacy
        // path. This is the exact production trap that caused the 12-h
        // "no signals" outage — UI now surfaces it immediately so the
        // operator sees it before clicking Save & enable LIVE.
        <div
          data-testid="bot-settings-engine-path-warning"
          className="flex items-start gap-3 px-4 py-3 rounded-sm bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-[#FF3B30] text-sm"
        >
          <AlertTriangle size={16} strokeWidth={2} className="mt-0.5 shrink-0" />
          <div className="space-y-0.5">
            <div className="font-semibold uppercase tracking-wider text-xs">Profile engine disabled</div>
            <div className="text-[#FF3B30]/90">
              Active profile <span className="font-mono">{activeStrategy?.profile?.name || activeStrategy?.active_strategy_id?.slice(0, 8)}</span>{" "}
              is selected, but the live engine is still using legacy{" "}
              <span className="font-mono">entry_rules</span>. Set{" "}
              <span className="font-mono">USE_STRATEGY_PROFILE_ENGINE=true</span> and redeploy
              before enabling live mode.
            </div>
          </div>
        </div>
      ) : null}

      {/* Live-mode selected (already saved, or about to save) — surface the
          full safety checklist so the operator can never claim they didn't
          see it. Re-rendered live as gates resolve. */}
      {isLiveTarget ? (
        <div
          data-testid="bot-settings-live-checklist"
          className="rounded-sm bg-[#FF3B30]/10 border border-[#FF3B30]/40 p-4 space-y-2"
        >
          <div className="flex items-center gap-2 text-[#FF3B30] font-semibold uppercase tracking-wider text-xs">
            <ShieldAlert size={14} strokeWidth={2} />
            Live execution checklist
          </div>
          <div className="text-xs text-[#A0A0AB]">
            All five gates must pass before this mode can be saved.
            Click "Save settings" to run them and confirm.
          </div>
        </div>
      ) : null}

      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="space-y-2">
          <Label>Symbol</Label>
          <Input data-testid="symbol-input" value={s.symbol} disabled className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>

        <div className="space-y-2">
          <Label>Bot mode</Label>
          <Select
            value={s.bot_mode}
            onValueChange={(v) => {
              // Auto-coerce hedge_mode=false when switching to LBank live.
              // LBank is one-way only — the adapter pins positionType=2
              // and the backend rejects hedge_mode=true on save. We clear
              // the flag here so the form reflects the actual saved state
              // and the operator never sees a stale `true` next to the
              // disabled toggle.
              const next = { ...s, bot_mode: v };
              if (v === "lbank_live_enabled") next.hedge_mode = false;
              setS(next);
            }}
          >
            <SelectTrigger data-testid="bot-mode-select" className="bg-[#1A1A22] border-border rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="paper">Paper Trading</SelectItem>
              <SelectItem value="testnet">Binance Testnet</SelectItem>
              <SelectItem value="live_disabled">Live Disabled</SelectItem>
              <SelectItem value="live_enabled">Live Enabled (Binance)</SelectItem>
              <SelectItem value="lbank_live_enabled">Live Enabled (LBank)</SelectItem>
            </SelectContent>
          </Select>
          {LIVE_MODES.includes(s.bot_mode) ? (
            <div className="text-[10px] uppercase tracking-widest text-[#FF3B30] flex items-center gap-1.5 mt-1">
              <AlertTriangle size={11} strokeWidth={2} /> routes real orders — preflight required on save
            </div>
          ) : null}
        </div>

        {s.bot_mode === "lbank_live_enabled" ? (
          <div className="space-y-2" data-testid="lbank-execution-mode-wrapper">
            <Label>LBank execution mode</Label>
            <Select
              value={s.lbank_execution_mode || "futures"}
              onValueChange={(v) => setS({ ...s, lbank_execution_mode: v })}
            >
              <SelectTrigger
                data-testid="lbank-execution-mode-select"
                className="bg-[#1A1A22] border-border rounded-sm"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="futures">Normal Futures (/placeOrder)</SelectItem>
                <SelectItem value="lead">Lead Orders (/traderPlaceOrder)</SelectItem>
              </SelectContent>
            </Select>
            {(s.lbank_execution_mode || "futures") === "lead" ? (
              <div
                data-testid="lbank-lead-warning"
                className="text-[10px] uppercase tracking-widest text-[#FFB020] flex items-start gap-1.5 mt-1"
              >
                <AlertTriangle size={11} strokeWidth={2} className="mt-[1px] shrink-0" />
                <span>
                  Lead Orders require an LBank copy-trading leader account
                  enabled in the LBank web UI. The bot will fail-closed (no
                  fallback to /placeOrder) if the lead endpoint rejects.
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="space-y-2">
          <Label>Order size mode</Label>
          <Select value={s.order_size_mode} onValueChange={(v) => setS({ ...s, order_size_mode: v })}>
            <SelectTrigger data-testid="order-size-mode-select" className="bg-[#1A1A22] border-border rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="usdt">Fixed USDT</SelectItem>
              <SelectItem value="percent">Percent of balance</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Order size value ({s.order_size_mode === "percent" ? "% of balance" : "USDT"})</Label>
          <Input
            data-testid="order-size-input"
            type="number"
            step="0.01"
            value={s.order_size_value}
            onChange={(e) => setS({ ...s, order_size_value: parseFloat(e.target.value) })}
            className="bg-[#1A1A22] border-border rounded-sm font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label>Leverage</Label>
          <Input
            data-testid="leverage-input"
            type="number"
            min="1"
            max="125"
            value={s.leverage}
            onChange={(e) => setS({ ...s, leverage: parseInt(e.target.value || "1", 10) })}
            className="bg-[#1A1A22] border-border rounded-sm font-mono"
          />
        </div>

        <div className="space-y-2">
          <Label>Signal interval (seconds)</Label>
          <Input
            data-testid="signal-interval-input"
            type="number"
            min="5"
            value={s.signal_interval_seconds}
            onChange={(e) => setS({ ...s, signal_interval_seconds: parseInt(e.target.value || "30", 10) })}
            className="bg-[#1A1A22] border-border rounded-sm font-mono"
          />
        </div>

        <div className="md:col-span-2 flex items-center justify-between border-t border-border pt-4">
          <div>
            <div className="text-sm font-medium">
              Hedge mode
              {s.bot_mode === "lbank_live_enabled" ? (
                <span
                  data-testid="hedge-mode-lbank-badge"
                  className="text-[10px] text-[#FF3B30] uppercase tracking-widest ml-1.5"
                >
                  Forced off for LBank — one-way mode only
                </span>
              ) : (
                <span className="text-[10px] text-[#F59E0B] uppercase tracking-widest ml-1.5">Paper only</span>
              )}
            </div>
            <div className="text-xs text-[#A0A0AB]">
              {s.bot_mode === "lbank_live_enabled" ? (
                <>
                  LBank account is pinned to one-way (net) position mode by the
                  adapter; hedge mode cannot be enabled here. The exchange is
                  the source of truth — a single net position per symbol.
                </>
              ) : (
                <>
                  Applies only to Paper Trading (simultaneous LONG + SHORT simulation).
                  On Binance Testnet / Live, the exchange is the source of truth — enable hedge mode on the exchange directly.
                </>
              )}
            </div>
          </div>
          <Switch
            data-testid="hedge-mode-switch"
            checked={s.bot_mode === "lbank_live_enabled" ? false : s.hedge_mode}
            disabled={s.bot_mode === "lbank_live_enabled"}
            onCheckedChange={(v) => setS({ ...s, hedge_mode: v })}
          />
        </div>
      </div>

      <Button
        data-testid="save-settings-btn"
        onClick={onSaveClick}
        disabled={saving}
        className={`rounded-sm ${
          switchingToLive
            ? "bg-[#FF3B30] text-white hover:bg-[#E5352B]"
            : "bg-white text-black hover:bg-[#A0A0AB]"
        }`}
      >
        {switchingToLive ? "Save & enable LIVE …" : "Save settings"}
      </Button>

      {/* Typed-confirmation dialog. Only opens when transitioning to a
          live mode — the four preflight gates run automatically; the
          confirm action is double-disabled (gates + typed string) to
          make it impossible to enable live trading by accident or by
          a fat-finger Enter on the form. */}
      <AlertDialog open={confirmOpen} onOpenChange={(o) => { if (!o) { setConfirmOpen(false); setConfirmText(""); } }}>
        <AlertDialogContent
          data-testid="bot-settings-live-confirm-dialog"
          className="bg-[#0F0F14] border-border rounded-sm max-w-lg"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-[#FF3B30]">
              <ShieldAlert size={18} strokeWidth={2} />
              Confirm live execution — {confirmTarget === "lbank_live_enabled" ? "LBank" : "Binance"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#A0A0AB]">
              You are about to switch the bot into a mode that routes <span className="text-white font-semibold">real orders</span> using your live exchange API key.
              All five gates below must pass and you must type the confirmation phrase to proceed.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-2 mt-2 text-xs" data-testid="bot-settings-preflight-gates">
            {[
              ["server_flag", "Server flag · LIVE_TRADING_ENABLED=true"],
              ["connection", `Connection · ${MODE_EXCHANGE[confirmTarget] || ""} test succeeds`],
              ["active_strategy", "Active strategy · profile selected"],
              ["profile_engine", "Profile engine · USE_STRATEGY_PROFILE_ENGINE=true"],
              ["balance", "Live balance · funds available"],
            ].map(([key, label]) => {
              const g = gates[key];
              const stateColor =
                g.state === "pass" ? "text-[#00E676] border-[#00E676]/40 bg-[#00E676]/10"
                : g.state === "fail" ? "text-[#FF3B30] border-[#FF3B30]/40 bg-[#FF3B30]/10"
                : g.state === "running" ? "text-[#A0A0AB] border-border bg-[#1A1A22]"
                : "text-[#71717A] border-border bg-[#1A1A22]";
              const Icon = g.state === "pass" ? Check : g.state === "fail" ? X : null;
              return (
                <div
                  key={key}
                  data-testid={`bot-settings-gate-${key.replace("_", "-")}`}
                  data-gate-state={g.state}
                  className={`flex items-start gap-2 px-3 py-2 border rounded-sm ${stateColor}`}
                >
                  <div className="w-4 h-4 mt-0.5 shrink-0 flex items-center justify-center">
                    {Icon ? <Icon size={14} strokeWidth={2} /> : (
                      g.state === "running"
                        ? <span className="inline-block w-3 h-3 border-2 border-[#71717A] border-t-white rounded-full animate-spin" />
                        : <span className="inline-block w-1.5 h-1.5 rounded-full bg-current opacity-50" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium uppercase tracking-wider text-[10px]">{label}</div>
                    <div className="text-[11px] normal-case text-[#A0A0AB] break-words mt-0.5">
                      {g.message || (g.state === "running" ? "checking…" : "—")}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-[#71717A]">
              Type <span className="font-mono text-[#FF3B30]">CONFIRM LIVE</span> to proceed
            </Label>
            <Input
              data-testid="bot-settings-live-confirm-input"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="CONFIRM LIVE"
              className="bg-[#1A1A22] border-border rounded-sm font-mono"
              autoComplete="off"
              autoFocus
            />
          </div>

          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel
              data-testid="bot-settings-live-confirm-cancel"
              disabled={saving}
              className="rounded-sm"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="bot-settings-live-confirm-ok"
              disabled={saving || running || !allGatesPass || confirmText !== "CONFIRM LIVE"}
              onClick={persistSave}
              className="bg-[#FF3B30] text-white hover:bg-[#E5352B] rounded-sm disabled:opacity-40"
            >
              {saving ? "Saving…" : "Save & enable LIVE"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
